import {
  GAMEPAD,
  connectedGamepad,
  gamepadButtonDown,
  readGamepadMove,
  risingEdge
} from "./gamepad";
import { getActiveInputDevice, subscribeActiveInputDevice } from "./inputDevice";

export type MenuAxis = -1 | 0 | 1;

export type MenuLayout = {
  characterCount: number;
  characterCols: number;
  selectedCharacter: number;
  /** Item count for each row of the setup panel, top to bottom. */
  actionRows: readonly number[];
};

export function menuAxisPulse(previous: MenuAxis, next: MenuAxis): MenuAxis {
  if (next === 0 || next === previous) return 0;
  return next;
}

export function cycleSelectIndex(selectedIndex: number, optionCount: number, direction: -1 | 1): number {
  if (optionCount <= 0) return 0;
  return (selectedIndex + direction + optionCount) % optionCount;
}

export function menuKeyMove(key: string, typing: boolean): { x: MenuAxis; y: MenuAxis } | null {
  if (key === "ArrowLeft") return { x: -1, y: 0 };
  if (key === "ArrowRight") return { x: 1, y: 0 };
  if (key === "ArrowUp") return { x: 0, y: -1 };
  if (key === "ArrowDown") return { x: 0, y: 1 };
  if (typing) return null;
  const letter = key.toLowerCase();
  if (letter === "a") return { x: -1, y: 0 };
  if (letter === "d") return { x: 1, y: 0 };
  if (letter === "w") return { x: 0, y: -1 };
  if (letter === "s") return { x: 0, y: 1 };
  return null;
}

export function menuPrimarySelector(lobbyOpen: boolean): "#ready-up" | "#practice" {
  return lobbyOpen ? "#ready-up" : "#practice";
}

export function nextMenuIndex(
  index: number,
  move: { x: MenuAxis; y: MenuAxis },
  layout: MenuLayout
): number {
  const total = layout.characterCount + actionTotal(layout);
  if (total <= 0) return 0;
  const current = clamp(index, 0, total - 1);
  const axis = move.x !== 0 ? "x" : move.y !== 0 ? "y" : null;
  if (!axis) return current;
  const dir = (axis === "x" ? move.x : move.y) as -1 | 1;

  if (current < layout.characterCount) {
    return moveInCharacters(current, axis, dir, layout);
  }
  return moveInActions(current, axis, dir, layout);
}

export function bindMenuGamepad(options: {
  menu: HTMLElement;
  joinPanel: HTMLElement;
  isBlocked?: () => boolean;
  view?: Window;
}): () => void {
  const view = options.view ?? window;
  let index = 0;
  let axisX: MenuAxis = 0;
  let axisY: MenuAxis = 0;
  let confirmDown = false;
  let backDown = false;
  let startDown = false;
  let frame = 0;

  const blocked = (): boolean => Boolean(options.menu.hidden) || Boolean(options.isBlocked?.());

  const lobbyOpen = (): boolean => options.joinPanel.classList.contains("lobby-open");
  let rows: readonly number[] = [];
  const items = (): HTMLElement[] => {
    const collected = collectMenuItems(options.menu, lobbyOpen());
    rows = collected.rows;
    return collected.items;
  };

  const layoutFor = (list: HTMLElement[]): MenuLayout => {
    const characterCount = list.filter((item) => item.classList.contains("character-card")).length;
    return {
      characterCount,
      actionRows: rows,
      characterCols: characterColumns(view),
      selectedCharacter: selectedCharacterIndex(list, characterCount)
    };
  };

  const focusPrimary = (list: HTMLElement[]): void => {
    const primary = options.menu.querySelector<HTMLElement>(menuPrimarySelector(lobbyOpen()));
    const next = primary ? list.indexOf(primary) : -1;
    if (next < 0) return;
    index = next;
    paint(list);
  };

  const paint = (list: HTMLElement[] = items()): void => {
    if (list.length === 0) return;
    index = clamp(index, 0, list.length - 1);
    const directional = getActiveInputDevice() !== "touch";
    for (const [itemIndex, item] of list.entries()) {
      item.classList.toggle("is-cursor", itemIndex === index && directional);
    }
    const active = list[index];
    if (directional && active && view.document.activeElement !== active) {
      active.focus({ preventScroll: true });
    }
  };

  const clearCursor = (): void => {
    for (const item of options.menu.querySelectorAll(".is-cursor")) {
      item.classList.remove("is-cursor");
    }
  };

  const moveCursor = (dx: MenuAxis, dy: MenuAxis): void => {
    const list = items();
    if (list.length === 0) return;
    const layout = layoutFor(list);
    const current = list[index];
    if (current instanceof HTMLSelectElement && dx !== 0) {
      current.selectedIndex = cycleSelectIndex(current.selectedIndex, current.options.length, dx);
      return;
    }
    const next = nextMenuIndex(index, { x: dx, y: dy }, layout);
    if (next === index) return;
    index = next;
    const target = list[index];
    if (target?.classList.contains("character-card")) target.click();
    paint(list);
  };

  const confirm = (): void => {
    const list = items();
    const target = list[index];
    if (!target) return;
    if (target instanceof HTMLSelectElement) {
      const primary = options.menu.querySelector<HTMLButtonElement>(menuPrimarySelector(lobbyOpen()));
      primary?.click();
      return;
    }
    if (target instanceof HTMLInputElement) {
      target.focus();
      return;
    }
    target.click();
    if (target.classList.contains("character-card")) focusPrimary(items());
  };

  const back = (): void => {
    const list = items();
    const layout = layoutFor(list);
    if (index >= layout.characterCount) {
      index = layout.selectedCharacter;
      paint(list);
    }
  };

  const start = (): void => {
    options.menu.querySelector<HTMLButtonElement>(menuPrimarySelector(lobbyOpen()))?.click();
  };

  const poll = (): void => {
    const pad = connectedGamepad();
    const move = readGamepadMove(pad);
    const dx = menuAxisPulse(axisX, move.x);
    const dy = menuAxisPulse(axisY, move.y);
    axisX = move.x;
    axisY = move.y;

    const confirmEdge = risingEdge(confirmDown, gamepadButtonDown(pad, GAMEPAD.A));
    const backEdge = risingEdge(backDown, gamepadButtonDown(pad, GAMEPAD.B));
    const startEdge = risingEdge(startDown, gamepadButtonDown(pad, GAMEPAD.START));
    confirmDown = confirmEdge.down;
    backDown = backEdge.down;
    startDown = startEdge.down;

    const typing = view.document.activeElement instanceof HTMLInputElement;
    if (!blocked() && getActiveInputDevice() === "gamepad") {
      if (typing) {
        if (backEdge.pressed) {
          if (view.document.activeElement instanceof HTMLElement) view.document.activeElement.blur();
          const list = items();
          index = layoutFor(list).selectedCharacter;
          paint(list);
        }
      } else {
        if (dx !== 0 || dy !== 0) moveCursor(dx, dy);
        if (confirmEdge.pressed) confirm();
        if (backEdge.pressed) back();
        if (startEdge.pressed) start();
      }
    }

    frame = view.requestAnimationFrame(poll);
  };

  const onFocusIn = (event: FocusEvent): void => {
    const list = items();
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const next = list.indexOf(target);
    if (next >= 0) {
      index = next;
      paint(list);
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (blocked() || event.metaKey || event.ctrlKey || event.altKey) return;
    const active = view.document.activeElement;
    const typing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
    if (event.key === "Enter" && !event.repeat && active instanceof HTMLElement && active.classList.contains("character-card")) {
      event.preventDefault();
      active.click();
      focusPrimary(items());
      return;
    }
    const move = menuKeyMove(event.key, typing);
    if (!move) return;
    event.preventDefault();
    if (typing && view.document.activeElement instanceof HTMLElement) view.document.activeElement.blur();
    moveCursor(move.x, move.y);
  };

  const unsubscribeDevice = subscribeActiveInputDevice((device) => {
    if (device === "gamepad" && !blocked()) {
      const list = items();
      index = layoutFor(list).selectedCharacter;
      paint(list);
    } else if (device === "touch") {
      clearCursor();
    }
  });

  options.menu.addEventListener("focusin", onFocusIn);
  view.addEventListener("keydown", onKeyDown);
  frame = view.requestAnimationFrame(poll);

  return () => {
    unsubscribeDevice();
    options.menu.removeEventListener("focusin", onFocusIn);
    view.removeEventListener("keydown", onKeyDown);
    view.cancelAnimationFrame(frame);
    clearCursor();
  };
}

const DIFFICULTY_SELECTORS = [
  "#difficulty-easy",
  "#difficulty-medium",
  "#difficulty-hard",
  "#difficulty-terminal"
];

const HEADER_ROW = ["#open-soundtrack", "#open-options"];

export function menuActionRows(lobbyOpen: boolean): string[][] {
  return lobbyOpen
    ? [["#copy-invite"], ["#player-name"], DIFFICULTY_SELECTORS, ["#ready-up"], ["#leave-lobby"], HEADER_ROW]
    : [
        ["#player-name"],
        DIFFICULTY_SELECTORS,
        ["#practice-start"],
        ["#practice"],
        ["#create-room", "#room-code", "#join-form button[type='submit']"],
        HEADER_ROW
      ];
}

function collectMenuItems(menu: HTMLElement, lobbyOpen: boolean): { items: HTMLElement[]; rows: number[] } {
  const characters = [...menu.querySelectorAll<HTMLElement>(".character-card")];
  const rows: number[] = [];
  const actions: HTMLElement[] = [];
  for (const row of menuActionRows(lobbyOpen)) {
    const found = row
      .map((selector) => menu.querySelector<HTMLElement>(selector))
      .filter((item): item is HTMLElement => item !== null && isNavigable(item));
    if (found.length === 0) continue;
    rows.push(found.length);
    actions.push(...found);
  }
  return { items: [...characters, ...actions], rows };
}

function isNavigable(item: HTMLElement): boolean {
  if (item.closest("[hidden]") || item.hasAttribute("disabled")) return false;
  return item.getClientRects().length > 0;
}

function selectedCharacterIndex(list: HTMLElement[], characterCount: number): number {
  const selected = list.slice(0, characterCount).findIndex((item) => item.classList.contains("selected"));
  return selected >= 0 ? selected : 0;
}

function characterColumns(view: Window): number {
  const grid = view.document.querySelector(".character-grid");
  if (!(grid instanceof HTMLElement)) return 9;
  const columns = view
    .getComputedStyle(grid)
    .gridTemplateColumns.split(" ")
    .filter((part) => part.length > 0 && part !== "none").length;
  return Math.max(1, columns);
}

function moveInCharacters(index: number, axis: "x" | "y", dir: -1 | 1, layout: MenuLayout): number {
  const cols = Math.max(1, layout.characterCols);
  const col = index % cols;
  const row = Math.floor(index / cols);
  const rows = Math.ceil(layout.characterCount / cols);

  if (axis === "x") {
    const rowStart = row * cols;
    const rowLength = Math.min(cols, layout.characterCount - rowStart);
    return rowStart + ((col + dir + rowLength) % rowLength);
  }

  const nextRow = row + dir;
  if (nextRow < 0) return index;
  if (nextRow >= rows) return actionTotal(layout) > 0 ? layout.characterCount : index;
  return Math.min(nextRow * cols + col, layout.characterCount - 1);
}

function moveInActions(index: number, axis: "x" | "y", dir: -1 | 1, layout: MenuLayout): number {
  const rows = layout.actionRows.filter((count) => count > 0);
  let remaining = index - layout.characterCount;
  let row = 0;
  while (row < rows.length - 1 && remaining >= (rows[row] ?? 0)) {
    remaining -= rows[row] ?? 0;
    row += 1;
  }
  const col = remaining;
  const rowStart = (target: number): number =>
    layout.characterCount + rows.slice(0, target).reduce((sum, count) => sum + count, 0);

  if (axis === "x") {
    const nextCol = col + dir;
    if (nextCol < 0 || nextCol >= (rows[row] ?? 0)) return index;
    return rowStart(row) + nextCol;
  }
  const nextRow = row + dir;
  if (nextRow < 0) return clampSelected(layout);
  if (nextRow >= rows.length) return index;
  const width = rows[nextRow] ?? 1;
  const fromWidth = rows[row] ?? 1;
  const nextCol =
    fromWidth === width
      ? col
      : fromWidth === 1
        ? 0
        : Math.min(width - 1, Math.floor(((col + 0.5) / fromWidth) * width));
  return rowStart(nextRow) + nextCol;
}

function actionTotal(layout: MenuLayout): number {
  return layout.actionRows.reduce((sum, count) => sum + Math.max(0, count), 0);
}

function clampSelected(layout: MenuLayout): number {
  if (layout.characterCount <= 0) return 0;
  return clamp(layout.selectedCharacter, 0, layout.characterCount - 1);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
